/**
 * SMOKE TEST CHECKLIST — RecentChecksTable
 *
 * Run these manually before releasing changes to the Recent Checks view.
 *
 * Rendering
 * [ ] Rows show: patient last, first / DOB / payer name + member ID / status badge / provider / date
 * [ ] "Suggested by VitalRev AI" sparkle shows next to payer when the resolved name differs from the raw shorthand (requestPayerName)
 * [ ] Error rows show red "Error" badge and open the failure slideout on click
 * [ ] Admin sees Clinic column; non-admin does not
 * [ ] Clinic filter dropdown visible only for admin with access to >1 clinic
 * [ ] Empty state: "No eligibility checks found." shown when list is empty
 * [ ] Error banner shown when error prop is non-null
 *
 * Interaction
 * [ ] Clicking a row with a result opens the MedicalSlideout
 * [ ] Returning from slideout lands back on Recent Checks (not the eligibility form)
 * [ ] Date column header sorts asc↑ / desc↓ on toggle; arrow icon updates
 * [ ] Sort header hidden (non-clickable) when only 1 row
 * [ ] "Load More" button visible when hasMore=true; hidden when false
 * [ ] "Loading…" spinner shown while loadingMore=true
 *
 * Filters
 * [ ] Search filters rows by patient name (any word order), DOB, member ID (dashes optional), payer, provider, clinic (case-insensitive)
 * [ ] Search with 0 matches names the loaded-check count and (when hasMore) shows Load More, which grows the searched set in place
 * [ ] Clinic filter "All Clinics" shows all; per-clinic filters to that clinic; "Unknown" shows null-clinicId rows
 * [ ] Changing clinic filter resets to first page (no stale results)
 *
 * Window filter
 * [ ] Default selection is "Today" on first mount
 * [ ] Today shows only checks with checkedAt >= today midnight in clinic TZ
 * [ ] Last 7 days shows today + 6 prior calendar days
 * [ ] Last 30 days shows today + 29 prior calendar days
 * [ ] All time shows all history (matches pre-change behavior)
 * [ ] Changing the window resets pagination and Load More cursor
 * [ ] Running a manual check makes the new row appear in Today view immediately
 * [ ] Empty state under Today + no checks reads "No eligibility checks in this window. Select 'All time' to see history."
 * [ ] Admin "All Clinics" + Today: rows include checks from each clinic's current calendar day
 * [ ] Admin clinic-filter change resets pagination
 * [ ] Admin specific-clinic + Last 7/30 days: the chosen window is honored — the
 *     table shows that clinic's older checks (server query filters by clinicId,
 *     so the 25-row page isn't truncated by other clinics' fresher rows).
 */

import type { Clinic, CorrectionOverride, EligibilityCheckDoc, EligibilityResultWithMembers, Provider, UserPickOverride } from '../types'
import type { WindowOption } from '../utils/recentCheckWindow'
import { reasonCopyFor, type TerminalReason, type TerminalState } from '../../shared/terminalState'
import { DISPLAY_STATUS_META, ROLLUP_OF, isAutoRetrying, retryingReason, retryingStatusMeta, type EligibilityDisplayStatus } from '../../shared/eligibilityDisplayStatus'
import { readTerminal } from '../utils/terminalStateBadge'
import { payerProvenance } from '../utils/payerProvenance'
import { stripPayerIdSuffix } from '../utils/formatters'
import { buildFlowRelations } from '../utils/checkFlowRelations'
import { IconCheckCircle, IconXCircle, IconAlertTriangle, IconClock, IconRefresh, IconSparkle } from './Icons'

interface RecentChecksTableProps {
  /** Pre-filtered, pre-sorted checks from Dashboard — this component is display-only. */
  filteredChecks: EligibilityCheckDoc[]
  /** How many checks are fetched so far (pre-search count). Search runs client-side
   *  over only these, so the search-empty state names this number and offers Load
   *  More — staff must understand a miss may just mean "not loaded yet". */
  loadedCount: number
  /** Provider list — used only for the row-click lookup to pass a Provider to onViewDetails. */
  providers: Provider[]
  /** Full clinic list — used for the filter dropdown and Clinic column name display. */
  clinics: Clinic[]
  /** Whether to show the clinic filter dropdown (true when user has access to >1 clinic). */
  showClinicFilter: boolean
  onViewDetails: (result: EligibilityResultWithMembers, provider: Provider, patientId?: string, requestSubscriber?: { firstName: string; lastName: string; memberId: string; dateOfBirth: string }, collectionResult?: EligibilityCheckDoc['collectionResult'], checkId?: string, correction?: CorrectionOverride, rawAppointmentPayer?: string, checkedAt?: string, clinicId?: string, userPick?: UserPickOverride, legacyCollectionResult?: EligibilityCheckDoc['legacyCollectionResult'], stediPayerId?: string | null, checkRecovery?: Pick<EligibilityCheckDoc, 'inRecoveryLoop' | 'recoveryAction' | 'flowId' | 'smsOutreachAttempted'>) => void
  /** Open the VSP benefits slideout for a successful VSP check (`benefits`/`vspBenefits` set, no `result`). */
  onViewVspCheck: (check: EligibilityCheckDoc) => void
  /** Open the failure slideout for a check with no displayable data (no result + no benefits, or status=error/timeout). */
  onViewFailedCheck: (check: EligibilityCheckDoc) => void
  error: string | null
  hasMore: boolean
  loadingMore: boolean
  onLoadMore: () => void
  /** Server-backed sort direction for the Date column. */
  checksSort: 'asc' | 'desc'
  /** Toggles checksSort — triggers a server re-fetch in Dashboard. */
  onSortChange: () => void
  /** Free-text search query (controlled by Dashboard). */
  searchQuery: string
  onSearchChange: (q: string) => void
  /** Active clinic filter value: 'all' or a clinicId string. */
  clinicFilter: 'all' | string
  onClinicFilterChange: (v: 'all' | string) => void
  /** Whether the current user is an admin — controls clinic column/filter visibility. */
  isAdmin: boolean
  /** Active time-window selection. Default 'all' = no time bound. */
  windowOption: WindowOption
  onWindowOptionChange: (v: WindowOption) => void
}

/** TerminalState → canonical display status, so these four pills read the same
 *  label + color class as the cockpit badge from one source of truth. */
const TERMINAL_TO_DISPLAY: Record<TerminalState, EligibilityDisplayStatus> = {
  eligible: 'eligible',
  ineligible: 'not_eligible',
  needs_review: 'needs_review',
  error: 'error',
}

function TerminalPill({ state, reason, checkType }: { state: TerminalState; reason: TerminalReason; checkType?: string }) {
  const display = TERMINAL_TO_DISPLAY[state]
  const meta = DISPLAY_STATUS_META[display]
  const icon = state === 'eligible' ? <IconCheckCircle />
    : state === 'ineligible' ? <IconXCircle />
    : state === 'needs_review' ? <IconAlertTriangle />
    : <IconRefresh />
  return (
    <span className={`recent-status recent-status--${ROLLUP_OF[display]}`} title={reasonCopyFor(reason, checkType)}>
      {icon} {meta.label}
    </span>
  )
}

/** Auto-retry pill (#289): label AND icon come from the shared meta, so this
 *  table can't drift from the Cockpit badge if the retry presentation ever
 *  changes in shared/eligibilityDisplayStatus.ts. */
function RetryingPill({ check }: { check: EligibilityCheckDoc }) {
  const meta = retryingStatusMeta(check)
  const icon = {
    check: <IconCheckCircle />,
    x: <IconXCircle />,
    alert: <IconAlertTriangle />,
    refresh: <IconRefresh />,
    clock: <IconClock />,
    spinner: <IconRefresh />, // this table has no spinner element
  }[meta.iconKey]
  return (
    <>
      <span className="recent-status recent-status--neutral" title={retryingReason(check)}>
        {icon} {meta.label}
      </span>
      {/* Attempts used vs. total is only half the retry state staff need at a
          glance (#289) — "wait or act?" is answered by when the next attempt
          fires, so it rides under the pill instead of only in the tooltip. */}
      {meta.sublabel && <div className="recent-checks__cell-secondary">{meta.sublabel}</div>}
    </>
  )
}

/** Convert "MATHISEN" or "mathisen" → "Mathisen". Handles multi-word and hyphenated names. */
function toProperCase(s: string): string {
  return s.toLowerCase().replace(/(?:^|\s|-)(\w)/g, m => m.toUpperCase())
}

/** Payer-name words whose casing proper-casing would mangle ("VSP" → "Vsp"). */
const PAYER_CASING: Record<string, string> = {
  VSP: 'VSP', EYEMED: 'EyeMed', UHC: 'UHC', BCBS: 'BCBS', HMO: 'HMO', PPO: 'PPO',
}

/** Proper-case a payer name, restoring known acronyms/brand casing. */
function toPayerCase(s: string): string {
  return toProperCase(s).replace(/[A-Za-z]+/g, w => PAYER_CASING[w.toUpperCase()] ?? w)
}

/** Format DOB string (YYYY-MM-DD) as MM/DD/YYYY, avoiding timezone shift. */
function formatDob(dob: string): string {
  try {
    const d = new Date(dob + 'T00:00:00')
    if (isNaN(d.getTime())) return dob
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${mm}/${dd}/${d.getFullYear()}`
  } catch {
    return dob
  }
}

/** Format ISO timestamp as "Feb 15, 2026, 3:14 PM". */
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

export default function RecentChecksTable({
  filteredChecks,
  loadedCount,
  providers,
  clinics,
  showClinicFilter,
  onViewDetails,
  onViewVspCheck,
  onViewFailedCheck,
  error,
  hasMore,
  loadingMore,
  onLoadMore,
  checksSort,
  onSortChange,
  searchQuery,
  onSearchChange,
  clinicFilter,
  onClinicFilterChange,
  isAdmin,
  windowOption,
  onWindowOptionChange,
}: RecentChecksTableProps) {

  /** Build O(1) clinic name lookup from the passed array. */
  const clinicsById = new Map(clinics.map(c => [c.id, c]))

  /** Two rows for one patient-appointment are legitimate (a DOS re-check or a
   *  correction re-run is a new check in the same flow) — but they must not
   *  render as unrelated peers with conflicting statuses (#289). Keyed over
   *  exactly the rows this table draws. */
  const flowRelations = buildFlowRelations(filteredChecks)

  /** Pending (in-flight) checks are the only non-clickable rows. Everything else
   *  opens a slideout: success → medical/vision, failure → failure slideout. */
  function isRowClickable(check: EligibilityCheckDoc): boolean {
    return check.status !== 'pending'
  }

  function handleRowClick(check: EligibilityCheckDoc) {
    if (check.status === 'pending') return
    // Actively auto-retrying → open the failure slideout (which shows the retry
    // status + guidance). A lookup_error carries a (promoted) result object that
    // would otherwise route it to the coverage/medical view with no retry copy.
    if (isAutoRetrying(check)) { onViewFailedCheck(check); return }

    if (check.result) {
      // Prefer the check's original provider; fall back to any clinic provider;
      // last-resort synthesize a stand-in labeled from check.identity.doctorName
      // (only reached when the clinic has zero providers at all).
      const rawDoctorName = check.identity?.doctorName?.replace(/\s*\(.*\)$/, '').trim()
      const provider: Provider = providers.find(p => p.id === check.providerId) ?? providers[0] ?? {
        id: check.providerId || 'unknown',
        clinicId: check.clinicId ?? '',
        firstName: rawDoctorName || 'Background check',
        lastName: '',
        npi: '',
        phone: '',
        email: '',
      }
      // Recent Checks has no appointment context, so rawAppointmentPayer is
      // undefined — pass it explicitly so checkedAt/clinicId land in their slots.
      onViewDetails(check.result, provider, check.patientId, check.requestSubscriber, check.collectionResult, check.id, check.correction, undefined, check.checkedAt, check.clinicId, check.userPick, check.legacyCollectionResult, check.stediPayerId, check)
      return
    }

    const benefits = check.benefits ?? check.vspBenefits
    if (benefits && check.status === 'success' && check.success === true) {
      onViewVspCheck(check)
      return
    }

    // Failed check — no result, no benefits. Open the failure slideout.
    onViewFailedCheck(check)
  }

  return (
    <main className="dashboard-main">
      <div className="recent-checks">
        <h2 className="recent-checks__title">Recent Eligibility Checks</h2>

        {/* ── Toolbar: segmented time window + clinic filter, search on the right ── */}
        <div className="recent-checks__toolbar">
          <div className="recent-checks__segmented" role="group" aria-label="Time window">
            {([
              ['today', 'Today'],
              ['last7days', '7 days'],
              ['last30days', '30 days'],
              ['all', 'All time'],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`recent-checks__segment${windowOption === value ? ' recent-checks__segment--active' : ''}`}
                aria-pressed={windowOption === value}
                onClick={() => onWindowOptionChange(value)}
              >
                {label}
              </button>
            ))}
          </div>
          {isAdmin && showClinicFilter && (
            <select
              className="recent-checks__clinic-filter"
              value={clinicFilter}
              onChange={e => onClinicFilterChange(e.target.value)}
            >
              <option value="all">All Clinics</option>
              {clinics.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          <div className="recent-checks__search-wrap">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" />
              <path d="M9.5 9.5L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              className="recent-checks__search"
              type="search"
              placeholder="Search name, DOB, member ID, payer…"
              value={searchQuery}
              onChange={e => onSearchChange(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <p className="error-banner__text">{error}</p>
          </div>
        )}

        {!error && filteredChecks.length === 0 && (
          searchQuery.trim()
            ? (
              // Search miss: search only sees the pages fetched so far, so say how
              // many were searched and offer Load More right here — loading more
              // grows the searched set and the filter re-applies automatically.
              <div className="recent-checks__empty">
                <p className="recent-checks__empty-title">
                  No matches for &ldquo;{searchQuery.trim()}&rdquo; in the {loadedCount} loaded {loadedCount === 1 ? 'check' : 'checks'}.
                </p>
                {hasMore ? (
                  <>
                    <p className="recent-checks__empty-sub">Search only covers the checks loaded so far. Load more to search older checks.</p>
                    <div className="recent-checks__load-more">
                      <button
                        className="recent-checks__load-more-btn"
                        disabled={loadingMore}
                        onClick={onLoadMore}
                      >
                        {loadingMore ? (
                          <>
                            <span className="recent-checks__inline-spinner" />
                            Loading...
                          </>
                        ) : (
                          'Load More'
                        )}
                      </button>
                    </div>
                  </>
                ) : windowOption !== 'all' ? (
                  <p className="recent-checks__empty-sub">All checks in this window are loaded. Select &ldquo;All time&rdquo; to search further back.</p>
                ) : (
                  <p className="recent-checks__empty-sub">All checks are loaded.</p>
                )}
              </div>
            )
            : windowOption !== 'all' && clinicFilter === 'all'
              ? (
                <div className="recent-checks__empty">
                  <p className="recent-checks__empty-title">No eligibility checks in this window.</p>
                  <p className="recent-checks__empty-sub">Select &ldquo;All time&rdquo; to see history.</p>
                </div>
              )
              : <p className="recent-checks__empty">No eligibility checks found.</p>
        )}

        {!error && filteredChecks.length > 0 && (
          <>
            <div className="recent-checks__table-wrapper">
              <table className="recent-checks__table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Payer</th>
                    <th>Status</th>
                    <th>Provider</th>
                    {isAdmin && <th>Clinic</th>}
                    <th
                      className={filteredChecks.length > 1 ? 'sortable-th sortable-th--active' : ''}
                      onClick={filteredChecks.length > 1 ? onSortChange : undefined}
                    >
                      Date
                      {filteredChecks.length > 1 && <span className="sort-arrow">{checksSort === 'desc' ? '↓' : '↑'}</span>}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredChecks.map(check => {
                    const provider = providers.find(p => p.id === check.providerId)
                    // Fallback: RPA checks don't write providerId — use identity.doctorName.
                    // EyeMed names include "(NPI-..., Tax ID–...)" — strip for display.
                    const rawDoctorName = check.identity?.doctorName
                      ? check.identity.doctorName.replace(/\s*\(.*\)$/, '')
                      : ''
                    const providerLabel = provider
                      ? `${toProperCase(provider.lastName)}, ${toProperCase(provider.firstName)}`
                      : rawDoctorName ? toProperCase(rawDoctorName) : '—'
                    // Prefer requestSubscriber → 271 subscriber → identity (always written)
                    const req = check.requestSubscriber
                    const sub = check.result?.subscriber
                    const ident = check.identity
                    const firstName = req?.firstName || sub?.firstName || ident?.firstName || ''
                    const lastName = req?.lastName || sub?.lastName || ident?.lastName || ''
                    const patientName = (firstName || lastName)
                      ? `${toProperCase(lastName)}, ${toProperCase(firstName)}`
                      : '—'
                    const dob = req?.dateOfBirth || sub?.dateOfBirth || ident?.dob || ''
                    const patientDob = dob ? `DOB: ${formatDob(dob)}` : ''
                    const rawPayer = check.result?.payer?.name || check.payerName || ''
                    const payerName = rawPayer ? toPayerCase(stripPayerIdSuffix(rawPayer)) : '—'
                    // "Suggested by VitalRev AI" badge — mirrors DailyCockpit's isResolved:
                    // the resolved payer (check.payerName) differs from the raw shorthand the
                    // check ran against (check.requestPayerName). Both stripped of "(00932)"-style
                    // id suffixes so a suffix-only delta doesn't trip it. Absent requestPayerName
                    // (pre-alias-learning docs) → no badge, same graceful skip as the cockpit.
                    const resolvedPayer = stripPayerIdSuffix((check.payerName ?? '').trim())
                    const requestPayer = stripPayerIdSuffix((check.requestPayerName ?? '').trim())
                    // The alias-learning sweep's own rechecks (ALIAS_PROBE_MODE) ARE
                    // AI-resolved but store payerName == the shorthand, so the name
                    // comparison below can't catch them — flag the mode explicitly.
                    // Mode first — a staff correction preserves requestPayerName
                    // by design, so the name delta alone reads a human's edit as
                    // machine work (#314 AC8).
                    const payerDecidedBy = payerProvenance({
                      mode: check.mode, resolvedPayer, rawPayer: requestPayer,
                    })
                    const payerResolved = payerDecidedBy === 'ai'
                    const memberId = req?.memberId || sub?.memberId || ''
                    const clinic = check.clinicId ? clinicsById.get(check.clinicId) : undefined
                    const clinicName = clinic?.name ?? '—'

                    const relation = flowRelations.get(check.id)
                    const clickable = isRowClickable(check)
                    const terminal = check.status === 'pending' ? null : readTerminal(check)

                    return (
                      <tr
                        key={check.id}
                        className={`recent-checks__row ${clickable ? 'recent-checks__row--clickable' : ''}`}
                        onClick={() => handleRowClick(check)}
                      >
                        <td>
                          <div className="recent-checks__cell-primary">
                            {patientName}
                            {relation && (
                              <span className="recent-checks__relation" title={relation.title}>
                                {relation.chip}
                              </span>
                            )}
                          </div>
                          {patientDob && <div className="recent-checks__cell-secondary">{patientDob}</div>}
                        </td>
                        <td>
                          <div className="recent-checks__cell-primary">
                            {payerName}
                            {payerResolved && (
                              <span
                                className="recent-checks__ai-badge"
                                data-ai-tooltip={`Original payer name was "${requestPayer}"\nVitalRev AI matched it to this payer`}
                                aria-label={`Original payer name was "${requestPayer}" — VitalRev AI matched it to this payer`}
                              >
                                <IconSparkle />
                              </span>
                            )}
                            {payerDecidedBy === 'staff' && (
                              <span
                                className="recent-checks__ai-badge recent-checks__staff-badge"
                                data-ai-tooltip={`Original payer name was "${requestPayer || check.payerName}"\nCorrected by staff`}
                                aria-label="Corrected by staff"
                              >
                                ✎
                              </span>
                            )}
                          </div>
                          {memberId && <div className="recent-checks__cell-secondary">{memberId}</div>}
                        </td>
                        <td>
                          {isAutoRetrying(check)
                            ? /* #289: failed-awaiting-retry must not read as live work. */
                              <RetryingPill check={check} />
                            : check.status === 'pending' || !terminal
                            ? <span className="recent-status recent-status--neutral"><IconAlertTriangle /> Pending</span>
                            : <TerminalPill state={terminal.state} reason={terminal.reason} checkType={check.type} />
                          }
                        </td>
                        <td>{providerLabel}</td>
                        {isAdmin && <td>{clinicName}</td>}
                        <td>{formatDate(check.checkedAt)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <div className="recent-checks__load-more">
                <button
                  className="recent-checks__load-more-btn"
                  disabled={loadingMore}
                  onClick={onLoadMore}
                >
                  {loadingMore ? (
                    <>
                      <span className="recent-checks__inline-spinner" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  )
}
